import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import logo from '../../assets/waseeny-logo.png';
import api from '../../config/api';
import userAPI from '../../services/userAPI';
import { useTranslate } from '../../Hook/useTranslate';
import { useLang } from '../../context/LanguageContext';
import './OrderDetails.css';
import { buildTelHref } from '../../utils/phone';
import { formatMoney } from '../../utils/formatMoney';
import RiderChatModal from '../Common/RiderChatModal';
import FormBanner from '../Common/FormBanner';
import ReasonDialog from '../Common/ReasonDialog';

import usePublicSettings, { supportContactText } from '../../Hook/usePublicSettings';
const OrderDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const t = useTranslate();
  const supportSettings = usePublicSettings();
  const { lang } = useLang();
  const [order, setOrder] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [formBanner, setFormBanner] = useState({ type: '', text: '' });
  const [reasonDialog, setReasonDialog] = useState(null); // 'cancel' | 'refund' | null
  const [cancelReasons, setCancelReasons] = useState([]);

  // Cancellation reasons are managed in the admin panel (Customer / All).
  useEffect(() => {
    api.get('/cms/cancellation-reasons', { params: { userType: 'customer' } })
      .then((res) => setCancelReasons((res.data?.reasons || []).map((r) => r.reason).filter(Boolean)))
      .catch(() => setCancelReasons([]));
  }, []);

  const submitCancel = async (reason) => {
    try {
      const targetId = order?._id || order?.orderId || id;
      await api.post(`/orders/${targetId}/cancel`, { reason });
      setReasonDialog(null);
      setFormBanner({ type: 'success', text: t("Order cancelled successfully") });
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      setReasonDialog(null);
      setFormBanner({ type: 'error', text: err.response?.data?.message || t("Failed to cancel order") });
    }
  };

  const submitRefund = async (reason) => {
    try {
      const targetId = order?._id || order?.orderId || id;
      await userAPI.requestRefund(targetId, reason, order?.totalAmount, []);
      setReasonDialog(null);
      setFormBanner({ type: 'success', text: t("Refund request submitted successfully. Admin will review it shortly.") });
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      setReasonDialog(null);
      setFormBanner({ type: 'error', text: err.response?.data?.message || t("Failed to request refund") });
    }
  };

  const [cart] = useState(() => {
    if (location.state && location.state.cart) {
      return location.state.cart;
    }
    return [];
  });

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        let res;
        try {
          res = await api.get(`/orders/${id}/details`);
        } catch (detailErr) {
          res = await api.get(`/orders/${id}/customer`);
        }
        const fetchedOrder = res.data?.order || res.data?.data || res.data;
        if (fetchedOrder && (fetchedOrder._id || fetchedOrder.id)) {
          setOrder(fetchedOrder);
        } else {
          console.warn("Order object missing in response:", res.data);
        }
      } catch (err) {
        console.error("Order details fetch failed:", err);
        const errMsg = err.response?.data?.message || err.response?.data?.details || err.message;
        setFormBanner({ type: 'error', text: `Failed to load order details: ${errMsg}` });
      }
    };
    if (id) fetchOrder();
  }, [id]);

  const getLangText = (data) => {
    if (!data) return "";
    if (typeof data === 'string') return data;
    return data.en || data.de || data.ar || (data && Object.values(data)[0]) || "";
  };

  // Derived Dynamic Data
  const orderIdVal = order?._id || order?.orderId || id || "#7892020189";
  const itemsList = order?.items || order?.products || cart || [];
  const totalCount = itemsList.reduce((sum, i) => sum + (i.quantity || 1), 0);

  const calculatedSubtotal = itemsList.reduce((sum, item) => {
    const price = item.price || item.unitPrice || 0;
    return sum + (price * (item.quantity || 1));
  }, 0);
  const toMoneyNumber = (value, fallback = 0) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : fallback;
  };

  const bill = order?.bill || {};
  const subtotal = toMoneyNumber(bill.itemTotal ?? order?.itemTotal, calculatedSubtotal);
  const serviceFee = toMoneyNumber(bill.platformFee ?? order?.platformFee);
  const deliveryFee = toMoneyNumber(bill.deliveryFee ?? order?.deliveryFee);
  const discountAmount = toMoneyNumber(bill.discount ?? order?.discount);
  const tax = toMoneyNumber(bill.tax ?? order?.tax);
  const packaging = toMoneyNumber(bill.packaging ?? order?.packaging);
  const smallCartFee = toMoneyNumber(bill.smallCartFee ?? order?.smallCartFee);
  const surgeFee = toMoneyNumber(bill.surgeFee ?? order?.surgeFee);
  const tip = toMoneyNumber(bill.tip ?? order?.tip);

  // order?.bill?.totalAmount contains precise value. Otherwise we compute
  const grandTotal = toMoneyNumber(
    bill.totalAmount ?? bill.toPay ?? order?.totalAmount,
    subtotal + serviceFee + deliveryFee + tax + packaging + smallCartFee + surgeFee + tip - discountAmount
  );
  const paidAmount = toMoneyNumber(order?.paidAmount, grandTotal);

  // Restaurant details
  const restaurantName = getLangText(order?.restaurant?.name || order?.restaurantId?.name || "Restaurant");
  const restaurantCategory = getLangText(order?.restaurant?.category || "");
  const currencySymbol = order?.currencySymbol || order?.currency || '';

  // Address
  const addrObj = order?.deliveryAddress || {};
  const addrLine1 = addrObj.addressLine || addrObj.street || "";
  const addrLine2 = addrObj.city ? `${addrObj.city}, ${addrObj.state || ''}` : "";

  // Payment Info
  const payMethod = order?.paymentMethod || "Online";
  const orderDate = order?.createdAt ? new Date(order.createdAt) : new Date();

  const locale = lang === 'ar' ? 'ar-EG' : (lang === 'de' ? 'de-DE' : 'en-US');
  const dateFormatted = orderDate.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  const timeFormatted = orderDate.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }).toUpperCase();
  const paymentTimeStr = `${t("On")} ${dateFormatted} ${t("at")} ${timeFormatted}`;

  const getImageUrl = (item) => {
    const rawImg = item.image || item.imageUrl;
    if (!rawImg) return "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?auto=format&fit=crop&w=100&h=100&q=80";
    if (typeof rawImg === 'object') return rawImg.en || rawImg.url || Object.values(rawImg)[0];
    return rawImg;
  };

  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const data = await userAPI.getProfile();
        setUserData(data);
      } catch (err) {
        console.error("Error fetching user profile:", err);
      }
    };
    fetchUser();
  }, []);

  const handleHelp = () => {
    setFormBanner({
      type: 'info',
      text: supportContactText(t, supportSettings),
    });
  };

  return (
    <div className="order-details-container">
      {/* Header */}
      <header className="order-details-header">
        <div className="order-details-logo-section">
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', marginRight: '10px' }}>
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <path d="M14 5l-6 6 6 6" stroke="#222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <img src={logo} alt="Waseeny" className="order-details-logo" onClick={() => navigate('/home')} />
          <span style={{ fontWeight: 700, fontSize: 16, color: "#111", letterSpacing: "-0.3px" }}>
            {t("orderSummary")}
          </span>
        </div>

        <div className="order-details-nav">
          <button onClick={handleHelp} className="order-details-nav-btn">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" stroke="#111" strokeWidth="1.8" />
              <path d="M7.5 7.5a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 3.5" stroke="#111" strokeWidth="1.8" strokeLinecap="round" />
              <circle cx="10" cy="14.5" r="1.2" fill="#111" />
            </svg>
            <span>{t("Help")}</span>
          </button>
          <button onClick={() => navigate('/profile')} className="order-details-nav-btn">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="7" r="4" stroke="#111" strokeWidth="1.8" />
              <path d="M3 18c0-3.866 3.134-7 7-7s7 3.134 7 7" stroke="#111" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            <span>{userData?.name || t("Profile")}</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="order-details-main">
        <FormBanner
          message={formBanner.text}
          type={formBanner.type || 'error'}
          onDismiss={() => setFormBanner({ type: '', text: '' })}
        />

        {/* Restaurant Header */}
        <div className="restaurant-header-section">
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: "#111", margin: "0 0 6px 0", letterSpacing: "-0.3px" }}>
              {restaurantName}
            </h2>
            <p style={{ fontSize: 13, color: "#666", margin: "0 0 8px 0" }}>{restaurantCategory}</p>
            <div
              style={{ fontSize: 13, color: "#EF4444", fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
              onClick={() => navigate(-1)}
            >
              {t("Menu")} <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <button
              className="call-btn"
              onClick={() => {
                const riderPhone = order?.callNumber || order?.riderPhone || order?.callContacts?.riderPhone || order?.rider?.phone;
                const restaurantPhone = order?.restaurant?.contactNumber || order?.restaurant?.phone || order?.restaurantId?.contactNumber;

                if (riderPhone) {
                  window.location.href = buildTelHref(riderPhone);
                } else if (restaurantPhone) {
                  window.location.href = buildTelHref(restaurantPhone);
                } else {
                  setFormBanner({ type: 'error', text: t('No contact number available at the moment.') });
                }
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
              {(order?.callNumber || order?.riderPhone || order?.rider?.phone) ? t('Call Rider') : t('Call Restaurant')}
            </button>

            {(order?.rider || order?.callNumber || order?.riderPhone) && (
              <button
                className="chat-rider-btn"
                onClick={() => setIsChatOpen(true)}
                style={{
                  padding: '9px 16px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  backgroundColor: '#ffffff',
                  color: '#0f172a',
                  fontWeight: '700',
                  fontSize: '13px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                  transition: 'all 0.2s'
                }}
              >
                💬 {t('Chat with Rider')}
              </button>
            )}
          </div>
        </div>

        {/* Heat Banner */}
        <div className="heat-banner">
          <span style={{ fontSize: 16 }}>🔥</span>
          <span style={{ fontSize: 12.5, color: "#333", fontWeight: 500 }}>{t("Your food is being prepared fresh!")}</span>
        </div>

        {/* Items Card */}
        <div className="details-card">
          <h3>{t("Items")} ({totalCount})</h3>
          {itemsList.map((item, idx) => (
            <div key={idx} className="order-item-row">
              <img src={getImageUrl(item)} alt={item.name} className="item-img" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111", marginBottom: 4 }}>{getLangText(item.name)}</div>
                <div style={{ fontSize: 13, color: "#777" }}>{getLangText(item.variant || item.description || "Original")}</div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#111" }}>
                {formatMoney((item.price || item.unitPrice || 0) * (item.quantity || 1), { symbol: currencySymbol, currency: order?.currency })}
              </div>
            </div>
          ))}
        </div>

        {/* Bill Details Card */}
        <div className="details-card">
          <h3>{t("billDetails")}</h3>

          <div className="bill-row-flex">
            <span>Subtotal</span>
            <span style={{ fontWeight: 700 }}>{formatMoney(subtotal, { symbol: currencySymbol, currency: order?.currency })}</span>
          </div>
          <div className="bill-row-flex">
            <span>Standard Delivery</span>
            <span style={{ color: "#EF4444", fontWeight: 600 }}>{deliveryFee > 0 ? formatMoney(deliveryFee, { symbol: currencySymbol, currency: order?.currency }) : 'Free'}</span>
          </div>
          <div className="bill-row-flex">
            <span>Service Fee</span>
            <span style={{ fontWeight: 700 }}>{formatMoney(serviceFee, { symbol: currencySymbol, currency: order?.currency })}</span>
          </div>
          {packaging > 0 && (
            <div className="bill-row-flex">
              <span>Packaging</span>
              <span style={{ fontWeight: 700 }}>{formatMoney(packaging, { symbol: currencySymbol, currency: order?.currency })}</span>
            </div>
          )}
          {smallCartFee > 0 && (
            <div className="bill-row-flex">
              <span>Small Cart Fee</span>
              <span style={{ fontWeight: 700 }}>{formatMoney(smallCartFee, { symbol: currencySymbol, currency: order?.currency })}</span>
            </div>
          )}
          {surgeFee > 0 && (
            <div className="bill-row-flex">
              <span>Surge Fee</span>
              <span style={{ fontWeight: 700 }}>{formatMoney(surgeFee, { symbol: currencySymbol, currency: order?.currency })}</span>
            </div>
          )}
          {tip > 0 && (
            <div className="bill-row-flex">
              <span>Tip</span>
              <span style={{ fontWeight: 700 }}>{formatMoney(tip, { symbol: currencySymbol, currency: order?.currency })}</span>
            </div>
          )}
          {tax > 0 && (
            <div className="bill-row-flex">
              <span>Tax</span>
              <span style={{ fontWeight: 700 }}>{formatMoney(tax, { symbol: currencySymbol, currency: order?.currency })}</span>
            </div>
          )}

          <div className="grand-total-row">
            <span style={{ fontSize: 13, fontWeight: 700, color: "#111" }}>Grand Total</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#111" }}>{formatMoney(grandTotal, { symbol: currencySymbol, currency: order?.currency })}</span>
          </div>
          {discountAmount > 0 && (
            <div className="bill-row-flex" style={{ color: "#EF4444" }}>
              <span>Coupon Applied</span>
              <span style={{ fontWeight: 700 }}>-{formatMoney(discountAmount, { symbol: currencySymbol, currency: order?.currency })}</span>
            </div>
          )}

          <div className="bill-row-flex" style={{ borderTop: "1px solid #eee", paddingTop: "16px" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#111" }}>Paid Amount</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#111" }}>{formatMoney(paidAmount, { symbol: currencySymbol, currency: order?.currency })}</span>
          </div>

          {discountAmount > 0 && (
            <div style={{ background: "#FEF2F2", padding: "12px", borderRadius: 8, textAlign: "center", color: "#EF4444", fontSize: 11.5, fontWeight: 500, marginTop: "16px" }}>
              Hurray! You saved {formatMoney(discountAmount, { symbol: currencySymbol, currency: order?.currency })} on this order
            </div>
          )}
        </div>

        {/* Info Blocks Card */}
        <div className="info-blocks-card">
          <div className="info-block-item" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
            <div style={{ flex: 1, paddingRight: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, margin: "0 0 10px 0", color: "#111" }}>{t("deliveryAddress")}</h3>
              <div style={{ fontSize: 13, color: "#111", lineHeight: 1.6 }}>{addrLine1}</div>
              <div style={{ fontSize: 13, color: "#111", lineHeight: 1.6 }}>{addrLine2}</div>
            </div>
            <button
              onClick={() => navigate(`/live-tracking/${id}`, { state: { order } })}
              style={{ background: "#fff", border: "1px solid #ddd", borderRadius: 6, padding: "7px 12px", fontSize: 12.5, color: "#333", fontWeight: 600, cursor: "pointer" }}>
              {t("Track")}
            </button>

          </div>

          <div className="info-block-item">
            <h3 style={{ fontSize: 14, fontWeight: 700, margin: "0 0 8px 0", color: "#111" }}>{t("orderIdLabel")}</h3>
            <div style={{ fontSize: 13, color: "#111" }}>{orderIdVal}</div>
          </div>

          <div className="info-block-item">
            <h3 style={{ fontSize: 14, fontWeight: 700, margin: "0 0 8px 0", color: "#111" }}>{t("Payment Method")}</h3>
            <div style={{ fontSize: 13, color: "#111" }}>{payMethod}</div>
          </div>

          <div className="info-block-item">
            <h3 style={{ fontSize: 14, fontWeight: 700, margin: "0 0 8px 0", color: "#111" }}>{t("Payment Details")}</h3>
            <div style={{ fontSize: 13, color: "#111" }}>{paymentTimeStr}</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ marginTop: '30px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {['placed', 'accepted'].includes(order?.status) && (
            <button
              className="cancel-order-btn"
              onClick={() => setReasonDialog('cancel')}
            >
              {t("Cancel Order")}
            </button>
          )}

          {order?.status === 'delivered' && (
            <div className="refund-section">
              {['completed', 'approved', 'processed'].includes((order?.refund?.status || order?.refundStatus || '').toLowerCase()) ? (
                <div style={{
                  padding: '14px 20px',
                  borderRadius: '12px',
                  textAlign: 'center',
                  fontWeight: '700',
                  fontSize: '14px',
                  background: '#f0fdf4',
                  color: '#16a34a',
                  border: '1px solid #bbf7d0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}>
                  ✓ {t("Refund Approved & Credited to Wallet")} ({formatMoney(order.refund?.amount || order.totalAmount, { symbol: currencySymbol })})
                </div>
              ) : ['in_progress', 'pending', 'pending_admin', 'sent_to_resto', 'resto_verified'].includes((order?.refund?.status || order?.refundStatus || '').toLowerCase()) ? (
                <div style={{
                  padding: '14px 20px',
                  borderRadius: '12px',
                  textAlign: 'center',
                  fontWeight: '700',
                  fontSize: '14px',
                  background: '#fffbeb',
                  color: '#d97706',
                  border: '1px solid #fde68a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}>
                  ⏳ {t("Refund Request Submitted - Pending Admin Review")}
                </div>
              ) : (
                <button
                  className="refund-request-btn"
                  onClick={() => setReasonDialog('refund')}
                >
                  {order.refund?.status === 'rejected' ? t("Re-request Refund") : t("Report Problem / Request Refund")}
                </button>
              )}
            </div>
          )}
        </div>

        <ReasonDialog
          open={reasonDialog !== null}
          title={reasonDialog === 'refund' ? t("Why are you requesting a refund?") : t("Reason for cancellation?")}
          reasons={reasonDialog === 'cancel' ? cancelReasons : []}
          submitLabel={reasonDialog === 'refund' ? t("Submit Refund") : t("Cancel Order")}
          onSubmit={reasonDialog === 'refund' ? submitRefund : submitCancel}
          onClose={() => setReasonDialog(null)}
        />

        <RiderChatModal
          isOpen={isChatOpen}
          onClose={() => setIsChatOpen(false)}
          orderId={order?._id || order?.orderId || id}
          riderInfo={order?.rider}
        />
      </main>
    </div>
  );
};

export default OrderDetails;